import { useEffect, useMemo, useRef } from "react";
import type { CalendarEvent, CalendarViewMode } from "../types";
import { useSubjectAreas } from "../api/use-subject-areas";
import { eventServesCourse } from "../lib/accepted-course-ids";
import { eventCardStyle, type EventCardStyle } from "../lib/subject-area-color";
import {
  GRID_BODY_HEIGHT,
  GRID_GUTTER,
  GRID_HOUR_LABELS,
  GRID_PAD,
  GRID_PXM,
  GRID_T0_MIN,
  assignLanes,
  eventToZonedMinutes,
  getWeekDays,
  type LaidOutEvent,
} from "./calendar-grid-helpers";

interface CalendarGridProps {
  events: CalendarEvent[];
  timeZone: string;
  weekStart: Date;
  viewMode: CalendarViewMode;
  selectedDay: Date;
  /**
   * Optional schedule id. When provided, the grid fetches subject areas via
   * `useSubjectAreas` and colors each event card by `event.subjectArea` →
   * `SubjectArea.colorHex`. Omit to keep the gray baseline (e.g. in tests
   * that don't care about coloring).
   */
  scheduleId?: string;
  /**
   * Phase 5 affordance — when supplied, event tiles become keyboard- and
   * mouse-activatable buttons that open the EventDetailPanel via the parent.
   */
  onEventClick?: (event: CalendarEvent) => void;
  /**
   * When set, every event serving this course code (primary or additional —
   * see {@link eventServesCourse}) gets a highlight ring. Drives the
   * "click a course reference in the issues tab → find it on the calendar"
   * affordance. Null/empty highlights nothing.
   */
  focusedCourseId?: string | null;
  /**
   * Bumped by the parent each time a focus is requested — even for the same
   * `focusedCourseId` — so the scroll-into-view effect re-fires on a repeat
   * click. (A plain value-equality effect would skip an identical re-request.)
   */
  focusNonce?: number;
}

/**
 * Normalize a subject-area name the same way the backend does
 * (`subjectArea.trim().ToLowerInvariant()`) so the FE lookup matches a
 * `SubjectArea.NormalizedName` row. Keep this aligned with
 * `SubjectAreasService.Normalize` on the backend.
 */
function normalizeSubjectArea(value: string | null | undefined): string | null {
  if (!value) return null;
  const trimmed = value.trim().toLowerCase();
  return trimmed.length > 0 ? trimmed : null;
}

const DAY_NAMES = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"] as const;
type DayNameIndex = 0 | 1 | 2 | 3 | 4 | 5 | 6;

const SHORT_MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
] as const;
type MonthIndex = 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11;

function dayNameFor(day: Date): string {
  // JS Date#getDay: 0=Sun..6=Sat. Convert to Mon=0..Sun=6.
  const idx = ((day.getDay() + 6) % 7) as DayNameIndex;
  return DAY_NAMES[idx];
}

function formatDayHeader(day: Date): string {
  const month = SHORT_MONTHS[day.getMonth() as MonthIndex];
  return `${dayNameFor(day)} ${month} ${day.getDate()}`;
}

function formatTimeRange(startMin: number, endMin: number): string {
  const fmt = (m: number) => {
    const h = Math.floor(m / 60);
    const mm = m % 60;
    const ap = h >= 12 ? "pm" : "am";
    const h12 = ((h + 11) % 12) + 1;
    return `${h12}:${mm.toString().padStart(2, "0")}${ap}`;
  };
  return `${fmt(startMin)} – ${fmt(endMin)}`;
}

interface EventCardProps {
  event: LaidOutEvent;
  dayCount: number;
  dayIndex: number;
  /**
   * Map keyed by `subjectArea.trim().toLowerCase()` → `colorHex`. The grid
   * builds this once per render from `useSubjectAreas` and passes it down,
   * so each card is a cheap `Map.get` instead of re-deriving the lookup.
   */
  subjectAreaColors: ReadonlyMap<string, string>;
  /** When supplied, the card becomes a button-role keyboard target. */
  onClick?: (event: CalendarEvent) => void;
  /**
   * When true, the card serves the currently focused course and gets a
   * highlight ring. Also stamps `data-highlighted="true"` so the grid's
   * scroll-into-view effect can find the first match.
   */
  highlighted?: boolean;
}

function EventCard({
  event,
  dayCount,
  dayIndex,
  subjectAreaColors,
  onClick,
  highlighted = false,
}: EventCardProps) {
  const { lane, lanes, startMin, endMin } = event;
  const top = (startMin - GRID_T0_MIN) * GRID_PXM + GRID_PAD + 1;
  const height = Math.max(12, (endMin - startMin) * GRID_PXM - 3);
  // A bright ring layered on top of the card's own border/color so a
  // highlighted event stands out without losing its subject-area coding.
  // `outline` sits outside the box and doesn't shift layout; the offset lifts
  // it off the border. z-index raises the ring above neighboring lanes.
  const highlightStyle: React.CSSProperties = highlighted
    ? {
        outline: "2px solid #facc15",
        outlineOffset: "1px",
        zIndex: 5,
      }
    : {};

  // Position within (full-width minus left gutter): start of this day-column
  // plus the per-lane offset inside that column.
  const k = (dayIndex + lane / lanes) / dayCount;
  const w = 1 / (dayCount * lanes);

  const malformed = !event.courseId; // missing-Course-ID is the only malformed cue in Phase 1

  // When the parent wires `onEventClick`, both card branches become
  // keyboard-activatable. Centralize the handlers so both renders agree.
  const interactiveProps = onClick
    ? {
        role: "button",
        tabIndex: 0,
        onClick: () => onClick(event),
        onKeyDown: (e: React.KeyboardEvent<HTMLDivElement>) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onClick(event);
          }
        },
      }
    : {};

  // Malformed (missing CourseId) styling is fixed — red dashed — regardless
  // of subject area. Phase 1 baseline; do not regress.
  if (malformed) {
    return (
      <div
        {...interactiveProps}
        data-testid={`calendar-event-${event.id}`}
        data-highlighted={highlighted ? "true" : undefined}
        className={`absolute rounded-[4px] px-1.5 py-1 text-[11px] leading-tight overflow-hidden bg-red-900/30 border border-dashed border-[#ef4444] text-red-300 flex flex-col gap-0.5${onClick ? " cursor-pointer" : ""}`}
        style={{
          top: `${top}px`,
          height: `${height}px`,
          left: `calc(${GRID_GUTTER}px + (100% - ${GRID_GUTTER}px) * ${k.toFixed(5)} + 2px)`,
          width: `calc((100% - ${GRID_GUTTER}px) * ${w.toFixed(5)} - 4px)`,
          ...highlightStyle,
        }}
      >
        <div
          className="font-medium whitespace-nowrap overflow-hidden text-ellipsis"
          title={event.summary}
        >
          {event.summary || "(no title)"}
        </div>
        <div className="text-red-300/70 text-[10px] whitespace-nowrap overflow-hidden text-ellipsis">
          {formatTimeRange(startMin, endMin)}
          {event.location ? ` · ${event.location}` : ""}
        </div>
      </div>
    );
  }

  const normalized = normalizeSubjectArea(event.subjectArea);
  const colorHex = normalized ? subjectAreaColors.get(normalized) : undefined;
  const cardStyle: EventCardStyle = eventCardStyle(colorHex);

  // The colors aren't Tailwind tokens, so we drive background/border/text via
  // inline style. The thick left border is the at-a-glance "categorized" cue.
  return (
    <div
      {...interactiveProps}
      data-testid={`calendar-event-${event.id}`}
      data-subject-color={colorHex ?? ""}
      data-highlighted={highlighted ? "true" : undefined}
      className={`absolute rounded-[4px] pl-2 pr-1.5 py-1 text-[11px] leading-tight overflow-hidden flex flex-col gap-0.5${onClick ? " cursor-pointer" : ""}`}
      style={{
        top: `${top}px`,
        height: `${height}px`,
        left: `calc(${GRID_GUTTER}px + (100% - ${GRID_GUTTER}px) * ${k.toFixed(5)} + 2px)`,
        width: `calc((100% - ${GRID_GUTTER}px) * ${w.toFixed(5)} - 4px)`,
        backgroundColor: cardStyle.background,
        borderLeft: `3px solid ${cardStyle.borderColor}`,
        borderTop: `1px solid ${cardStyle.borderColor}`,
        borderRight: `1px solid ${cardStyle.borderColor}`,
        borderBottom: `1px solid ${cardStyle.borderColor}`,
        color: cardStyle.color,
        ...highlightStyle,
      }}
    >
      <div
        className="font-medium whitespace-nowrap overflow-hidden text-ellipsis"
        title={event.summary}
      >
        {event.summary || "(no title)"}
      </div>
      <div
        className="text-[10px] whitespace-nowrap overflow-hidden text-ellipsis opacity-80"
        style={{ color: cardStyle.color }}
      >
        {formatTimeRange(startMin, endMin)}
        {event.location ? ` · ${event.location}` : ""}
      </div>
    </div>
  );
}

export function CalendarGrid({
  events,
  timeZone,
  weekStart,
  viewMode,
  selectedDay,
  scheduleId,
  onEventClick,
  focusedCourseId,
  focusNonce,
}: CalendarGridProps) {
  const bodyRef = useRef<HTMLDivElement>(null);
  // Guards against re-scrolling for a focus request we've already handled: on
  // an unrelated re-render (e.g. a background events refetch) or a tab-return
  // remount we must not yank the viewport again for the same nonce.
  const scrolledNonceRef = useRef<number | undefined>(undefined);

  // Fetch subject areas at the grid level (one query per render-tree, not one
  // per card). `useSubjectAreas` no-ops when the id is empty, so the calendar
  // works in test/preview contexts that don't supply a scheduleId.
  const subjectAreasQuery = useSubjectAreas(scheduleId ?? "", {
    enabled: !!scheduleId,
  });
  const subjectAreaColors = useMemo(() => {
    const map = new Map<string, string>();
    for (const area of subjectAreasQuery.data ?? []) {
      const key = area.name.trim().toLowerCase();
      if (key) map.set(key, area.colorHex);
    }
    return map;
  }, [subjectAreasQuery.data]);
  // `days` is stable across renders as long as the inputs don't change. Memo
  // keeps the downstream `perDay.map` from churning every render.
  const days = useMemo<readonly Date[]>(
    () =>
      viewMode === "week" ? getWeekDays(weekStart) : [new Date(selectedDay)],
    [viewMode, weekStart, selectedDay],
  );
  const dayCount = days.length;

  // For each day, project events into local minutes (filtering to events that
  // intersect the day in the schedule timezone) and assign overlap lanes.
  const perDay = useMemo(
    () =>
      days.map((day) => {
        const projected = events
          .map((ev) => {
            const minutes = eventToZonedMinutes(ev, day, timeZone);
            if (!minutes) return null;
            return {
              ...ev,
              startMin: minutes.startMin,
              endMin: minutes.endMin,
            };
          })
          .filter(
            (ev): ev is CalendarEvent & { startMin: number; endMin: number } =>
              ev !== null,
          );
        return assignLanes(projected);
      }),
    [events, days, timeZone],
  );

  // After a focus request re-positions the grid, scroll the first highlighted
  // card into view. Keyed on `focusNonce` (not `focusedCourseId`) so clicking
  // the same course twice still scrolls. `perDay` is also a dependency because
  // a cross-week jump loads that week's events asynchronously — the highlighted
  // node doesn't exist on the first post-focus render, so we retry as `perDay`
  // updates and scroll once the node appears (recording the handled nonce so
  // later re-renders for the same request don't re-scroll). jsdom-guarded: the
  // ref may be null and `scrollIntoView` is not implemented there.
  useEffect(() => {
    if (focusNonce === undefined) return;
    if (scrolledNonceRef.current === focusNonce) return;
    const first = bodyRef.current?.querySelector<HTMLElement>(
      '[data-highlighted="true"]',
    );
    if (!first) return;
    scrolledNonceRef.current = focusNonce;
    first.scrollIntoView?.({ block: "center", behavior: "smooth" });
  }, [focusNonce, perDay]);

  return (
    <div
      data-testid="calendar-grid"
      className="bg-surface border border-border-subtle rounded-lg overflow-hidden"
    >
      {/* Day header row */}
      <div
        className="flex bg-page border-b border-border-subtle"
        data-testid="calendar-grid-header"
      >
        <div
          className="flex-shrink-0"
          style={{ width: `${GRID_GUTTER}px` }}
          aria-hidden="true"
        />
        {days.map((day, idx) => (
          <div
            key={day.toISOString()}
            className="flex-1 px-2 py-2.5 text-xs font-semibold text-text-soft border-l border-surface"
            data-testid={`calendar-day-header-${idx}`}
          >
            {formatDayHeader(day)}
          </div>
        ))}
      </div>

      {/* Grid body */}
      <div
        ref={bodyRef}
        className="relative"
        style={{ height: `${GRID_BODY_HEIGHT}px` }}
        data-testid="calendar-grid-body"
      >
        {/* Hour gridlines + labels */}
        {GRID_HOUR_LABELS.map((label, i) => (
          <div key={`hour-line-${label}`}>
            <div
              className="absolute border-t border-border-subtle/70"
              style={{
                top: `${i * 60 * GRID_PXM + GRID_PAD}px`,
                left: `${GRID_GUTTER}px`,
                right: 0,
              }}
              aria-hidden="true"
            />
            {i < GRID_HOUR_LABELS.length - 1 && (
              <div
                className="absolute border-t border-border-subtle/40"
                style={{
                  top: `${(i * 60 + 30) * GRID_PXM + GRID_PAD}px`,
                  left: `${GRID_GUTTER}px`,
                  right: 0,
                }}
                aria-hidden="true"
              />
            )}
            <div
              data-testid={`calendar-hour-label-${i}`}
              className="absolute text-right text-[10px] text-text-faint"
              style={{
                top: `${i * 60 * GRID_PXM - 6 + GRID_PAD}px`,
                left: 0,
                width: `${GRID_GUTTER - 6}px`,
              }}
            >
              {label}
            </div>
          </div>
        ))}

        {/* Day column separators */}
        {Array.from({ length: dayCount + 1 }).map((_, i) => (
          <div
            key={`day-sep-${i}`}
            className="absolute border-l border-border-subtle/70"
            style={{
              top: 0,
              bottom: 0,
              left: `calc(${GRID_GUTTER}px + (100% - ${GRID_GUTTER}px) * ${(i / dayCount).toFixed(5)})`,
            }}
            aria-hidden="true"
          />
        ))}

        {/* Events */}
        {perDay.map((laidOut, dayIndex) =>
          laidOut.map((ev) => (
            <EventCard
              key={`${ev.id}-${dayIndex}`}
              event={ev}
              dayCount={dayCount}
              dayIndex={dayIndex}
              subjectAreaColors={subjectAreaColors}
              onClick={onEventClick}
              highlighted={eventServesCourse(ev, focusedCourseId)}
            />
          )),
        )}
      </div>
    </div>
  );
}
